import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Users, 
  FileCheck, 
  Boxes, 
  FolderKanban, 
  PanelLeft, 
  LogOut,
  Truck,
  ClipboardList,
  Receipt,
  ArrowRightLeft,
  Activity,
  Mail,
  FileText,
  MailCheck,
  Share2,
  CreditCard,
  ImageIcon,
  Navigation,
  MessageSquare,
  PackageSearch,
  Search,
  X,
  Settings,
  Loader2,
  Database,
  Store,
  ShieldCheck,
  ScanBarcode
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import NotificationBell from '../../components/NotificationBell';

const API = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/tickets', label: 'Tickets', icon: MessageSquare },
  { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
  { path: '/admin/backorders', label: 'Backorders', icon: PackageSearch },
  { path: '/admin/customers', label: 'Customers & Users', icon: Users },
  { path: '/admin/trade-applications', label: 'Trade Apps', icon: FileCheck },
  { type: 'divider', label: 'Inventory' },
  { path: '/admin/products', label: 'Products', icon: Package },
  { path: '/admin/suppliers', label: 'Suppliers', icon: Truck },
  { path: '/admin/purchase-orders', label: 'Purchase Orders', icon: ClipboardList },
  { path: '/admin/stock-movements', label: 'Stock History', icon: ArrowRightLeft },
  { path: '/admin/label-designer', label: 'Label Designer', icon: ScanBarcode },
  { type: 'divider', label: 'Invoicing' },
  { path: '/admin/credit-accounts', label: 'Credit Accounts', icon: CreditCard },
  { path: '/admin/estimates', label: 'Estimates', icon: FileText },
  { path: '/admin/invoices', label: 'Invoices', icon: Receipt },
  { path: '/admin/payment-methods', label: 'Payment Methods', icon: CreditCard },
  { path: '/admin/vendors', label: 'Vendors', icon: Store },
  { type: 'divider', label: 'Content' },
  { path: '/admin/projects', label: 'Projects', icon: FolderKanban },
  { path: '/admin/pages', label: 'Page Builder', icon: PanelLeft },
  { path: '/admin/site-images', label: 'Site Images', icon: ImageIcon },
  { path: '/admin/navigation', label: 'Navigation', icon: Navigation },
  { path: '/admin/social-media', label: 'Social Media', icon: Share2 },
  { type: 'divider', label: 'System' },
  { path: '/admin/security', label: 'Security (2FA)', icon: ShieldCheck },
  { path: '/admin/store-settings', label: 'Store Settings', icon: Settings },
  { path: '/admin/backups', label: 'Database Backups', icon: Database },
  { path: '/admin/xero', label: 'Xero', icon: ArrowRightLeft },
  { path: '/admin/shipping', label: 'Shipping', icon: Truck },
  { path: '/admin/email-settings', label: 'Email Settings', icon: Mail },
  { path: '/admin/email-templates', label: 'Email Templates', icon: Mail },
  { path: '/admin/email-logs', label: 'Email Logs', icon: MailCheck },
  { path: '/admin/activity-log', label: 'Activity Log', icon: Activity },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Handle click outside to close search
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape') {
        setShowSearch(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search with debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }

    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(`${API}/api/admin/global-search?q=${encodeURIComponent(searchQuery)}`, authHeaders);
        setSearchResults(res.data);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleResultClick = (item) => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults(null);
    
    // For tickets, navigate directly to the detail page
    if (item.type === 'ticket') {
      navigate(item.url);
      return;
    }
    
    // Navigate with state to trigger the highlight
    const targetPath = item.url.split('?')[0];
    navigate(targetPath, { state: { highlightId: item.id, ts: Date.now() } });
  };

  const getTypeIcon = (type) => {
    const icons = {
      product: Package,
      customer: Users,
      ticket: MessageSquare,
      order: ShoppingCart,
      invoice: Receipt,
      estimate: FileText
    };
    return icons[type] || Package;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-500/20 text-blue-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      closed: 'bg-zinc-500/20 text-zinc-400',
      resolved: 'bg-green-500/20 text-green-400',
      draft: 'bg-zinc-500/20 text-zinc-400',
      sent: 'bg-blue-500/20 text-blue-400',
      paid: 'bg-green-500/20 text-green-400',
      shipped: 'bg-purple-500/20 text-purple-400',
      delivered: 'bg-green-500/20 text-green-400',
      processing: 'bg-yellow-500/20 text-yellow-400'
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-400';
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#121212] border-r border-zinc-800 flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/" className="text-lg font-black text-white uppercase tracking-tight">
            Tech<span className="text-[#78BE20]">Dist</span> Admin
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item, idx) => {
            if (item.type === 'divider') {
              return (
                <div key={idx} className="pt-4 pb-2">
                  <p className="px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{item.label}</p>
                </div>
              );
            }
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-2.5 rounded-sm transition-colors ${
                  isActive 
                    ? 'bg-[#78BE20] text-black' 
                    : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="h-4 w-4 mr-3" />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <p className="text-white font-medium">{user?.first_name}</p>
              <p className="text-zinc-500 text-xs">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar with Search */}
        <div className="sticky top-0 z-40 bg-[#0A0A0A]/95 backdrop-blur border-b border-zinc-800 px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 max-w-xl" ref={searchRef}>
              <div className="relative">
                <button
                  onClick={() => { setShowSearch(true); setTimeout(() => inputRef.current?.focus(), 100); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-500 hover:border-zinc-700 transition-colors"
                  data-testid="global-search-trigger"
                >
                  <Search className="h-4 w-4" />
                  <span className="text-sm">Search everything...</span>
                  <kbd className="ml-auto px-2 py-0.5 text-xs bg-zinc-800 rounded border border-zinc-700">⌘K</kbd>
                </button>

                {/* Search Modal/Dropdown */}
                {showSearch && (
                  <div className="absolute top-0 left-0 right-0 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
                      <Search className="h-4 w-4 text-zinc-500" />
                      <Input
                        ref={inputRef}
                        type="text"
                        placeholder="Search products, customers, tickets, orders..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 bg-transparent border-0 p-0 h-auto text-white placeholder:text-zinc-500 focus-visible:ring-0"
                        data-testid="global-search-input"
                      />
                      {searchLoading && <Loader2 className="h-4 w-4 text-zinc-500 animate-spin" />}
                      <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} className="text-zinc-500 hover:text-white">
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Search Results */}
                    {searchResults && (
                      <div className="max-h-96 overflow-y-auto">
                        {searchResults.total === 0 ? (
                          <div className="p-6 text-center text-zinc-500">
                            No results found for "{searchQuery}"
                          </div>
                        ) : (
                          <div className="p-2">
                            {Object.entries(searchResults.results).map(([category, items]) => {
                              if (items.length === 0) return null;
                              const categoryLabels = {
                                products: 'Products',
                                customers: 'Customers',
                                tickets: 'Tickets',
                                orders: 'Orders',
                                invoices: 'Invoices',
                                estimates: 'Estimates'
                              };
                              return (
                                <div key={category} className="mb-3">
                                  <div className="px-3 py-1.5 text-xs font-semibold text-zinc-500 uppercase">
                                    {categoryLabels[category]} ({items.length})
                                  </div>
                                  {items.map((item, idx) => {
                                    const Icon = getTypeIcon(item.type);
                                    return (
                                      <button
                                        key={idx}
                                        onClick={() => handleResultClick(item)}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors text-left"
                                        data-testid={`search-result-${item.type}-${idx}`}
                                      >
                                        <div className="p-1.5 bg-zinc-800 rounded">
                                          <Icon className="h-4 w-4 text-[#78BE20]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm text-white font-medium truncate">{item.title}</p>
                                          <p className="text-xs text-zinc-500 truncate">{item.subtitle}</p>
                                        </div>
                                        {item.status && (
                                          <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(item.status)}`}>
                                            {item.status}
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {!searchResults && searchQuery.length < 2 && (
                      <div className="p-4 text-center text-zinc-500 text-sm">
                        Type at least 2 characters to search
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right side - notifications and date */}
            <div className="flex items-center gap-4 ml-4">
              <NotificationBell />
              <span className="text-xs text-zinc-500">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
          </div>
        </div>

        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
